import React, { useEffect, useState } from 'react';
import {
  CheckCircleFilled,
  CloudDownloadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { message, UploadProps, type UploadFile } from 'antd';
import styles from './Uploader.module.css';
import { DarwinFlex, DarwinUpload } from 'components/darwin-ui';
import { useTranslation } from 'react-i18next';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { ErrorType } from 'types/genericTypes';
import { downloadBlob, fetchBlobFromApi } from 'api/apiClient';
import { EudrRegulationDocument } from './EudrRegulationSection';
import { useUploadEUDRDocs } from 'api/services/eudrDocumentsService';
import DarwinTypography from 'components/darwin-ui/Typography';
import { makeBeforeUpload } from 'utils/uploaderCommon';
import Icon from 'components/darwin-ui/Icon';
import { FOLDER_ORGANIZATIONS_EUDR_DOCUMENTS } from 'utils/eudrCommon';

const { Text } = DarwinTypography;

const MAX_SIZE_MB = 5;

type FileImageUploaderProps = {
  disabled?: boolean;
  required?: boolean;
  documentHandler: (id: string, type: EudrRegulationDocument) => void;
  removeDocumentHandler?: (id: string) => void;
  listedDocuments?: string[];
  type?: EudrRegulationDocument;
  folder?: string;
};

export default function FileImageUploader({
  disabled,
  required,
  documentHandler,
  removeDocumentHandler,
  listedDocuments,
  type,
  folder = FOLDER_ORGANIZATIONS_EUDR_DOCUMENTS,
}: FileImageUploaderProps) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const { mutateAsync: uploadEudrDocument } = useUploadEUDRDocs();

  useEffect(() => {
    if (listedDocuments) {
      const mapped: UploadFile[] = listedDocuments.map((doc: any) => ({
        uid: String(doc.id),
        name: String(doc.originalName),
        status: 'done',
        url: String(doc.signedUrl),
      }));
      setFileList(mapped);
    }
  }, [listedDocuments]);

  const handleDownload = async (url: string, fileName: string) => {
    try {
      const blob = await fetchBlobFromApi(url, {}, true);
      downloadBlob(blob, fileName);
      openNotification(
        t('success'),
        t('eudr-regulation.file-download-success'),
        'success'
      );
    } catch (error) {
      openNotification(
        t('error'),
        t('eudr-regulation.file-download-error'),
        'error'
      );
    }
  };

  const handleRetry = async (file: UploadFile) => {
    const rcFile = file.originFileObj;
    if (!rcFile) {
      message.error(t('eudr-regulation.file-upload-try-again-file-invalid'));
      return;
    }

    try {
      const response = await uploadEudrDocument({ file: rcFile, folder });
      message.success(t('eudr-regulation.file-upload-success'));
      const updatedFile: UploadFile = {
        name: rcFile.name,
        status: 'done',
        uid: file.uid,
      };
      setFileList(prevList =>
        prevList.map(f => (f.uid === file.uid ? updatedFile : f))
      );
      documentHandler(response?.data?.id, type || 'propertyDocuments');
    } catch (err) {
      const apiError = err as ErrorType;
      const errorMessage = apiError.body?.errorCode
        ? t(apiError.body.errorCode)
        : t('eudr-regulation.file-upload-error');
      message.error(errorMessage);
      const updatedFile: UploadFile = {
        name: rcFile.name,
        status: 'error',
        uid: file.uid,
      };
      setFileList(prevList =>
        prevList.map(f => (f.uid === file.uid ? updatedFile : f))
      );
    }
  };

  const uploadProps: UploadProps = {
    beforeUpload: makeBeforeUpload({
      t,
      allowedTypes: ['application/pdf', 'image/jpeg', 'image/png'],
      maxSizeMB: 5,
      onError: msg => message.error(msg),
      textMessages: {
        wrongType: 'eudr-regulation.file-type-not-allowed',
        sizeExceeded: 'eudr-regulation.file-size-exceeded',
      },
    }),

    fileList,

    customRequest: async options => {
      const { file, onSuccess, onError } = options;
      try {
        const res = await uploadEudrDocument({ file: file as File, folder });
        documentHandler(res?.data?.id, type || 'propertyDocuments');
        onSuccess?.(res);
      } catch (err) {
        const apiError = err as ErrorType;
        if (apiError.body?.errorCode) {
          openNotification(t('error'), t(apiError.body.errorCode), 'error');
        } else {
          openNotification(
            t('error'),
            t('eudr-regulation.file-upload-error'),
            'error'
          );
        }
        onError?.(err as any);
      }
    },

    itemRender: (_, file, __, actions) => {
      const isError = file.status === 'error';
      const isSuccess = file.status === 'done';
      const isDownloadable = !!file.url;

      return (
        <DarwinFlex className={styles.item}>
          <DarwinFlex className={styles.itemLeft}>
            <Icon name="uploadDocument" className={styles.itemIcon} color='black'/>
            <DarwinFlex vertical style={{ marginLeft: 8 }}>
              {isError ? (
                <>
                  <Text className={styles.errorText}>
                    {t('eudr-regulation.file-upload-error-description')}
                  </Text>
                  <Text>{file.name}</Text>
                </>
              ) : (
                <>
                  <Text className={styles.itemName}>{file.name}</Text>
                  <Text
                    onClick={() => handleDownload(file.url || '', file.name)}
                    className={styles.itemSubtle}
                  >
                    {file.name}
                  </Text>
                </>
              )}
            </DarwinFlex>
          </DarwinFlex>

          <DarwinFlex className={styles.itemActions}>
            {isSuccess && <CheckCircleFilled className={styles.successIcon} />}
            {isDownloadable && !isError && (
              <CloudDownloadOutlined
                onClick={() => handleDownload(file.url || '', file.name)}
              />
            )}
            {isError && (
              <Text className={styles.retry} onClick={() => handleRetry(file)}>
                {t('eudr-regulation.file-upload-try-again')}
              </Text>
            )}
            <DeleteOutlined
              className={styles.deleteIcon}
              onClick={() => actions.remove?.()}
            />
          </DarwinFlex>
        </DarwinFlex>
      );
    },

    maxCount: 10,
    multiple: true,

    onChange(info) {
      const newFileList = info.fileList.map(f => ({ ...f, status: f.status }));
      setFileList(newFileList);
    },

    onRemove: (file: UploadFile) => {
      removeDocumentHandler?.(file.uid || '');
    },
  };

  const isDisabled = disabled;
  const showErrorBorder = required && fileList.length === 0;

  return (
    <DarwinFlex vertical className={styles.wrapper}>
      <Text className={styles.title}>
        {t('eudr-regulation.file-upload-title')}
      </Text>

      <DarwinUpload
        disabled={isDisabled}
        className={`${styles.uploadRoot} ${isDisabled ? styles.disabled : ''}`}
        {...uploadProps}
      >
        <DarwinFlex
          className={`${styles.dropzone} ${
            showErrorBorder ? styles.dropzoneError : styles.dropzoneOk
          }`}
        >
          <DarwinFlex className={styles.row}>
            <DarwinFlex className={styles.dropIcon}>
              <Icon name="uploadDocument" size='xxl'/>
            </DarwinFlex>

            <DarwinFlex vertical style={{ alignItems: 'flex-start' }}>
              <Text className={styles.placeholder}>
                {t('eudr-regulation.file-upload-placeholder')}
              </Text>
              <Text
                className={styles.placeholder}
                style={{ textDecoration: 'underline' }}
              >
                {t('eudr-regulation.file-upload-placeholder-click')}
              </Text>
            </DarwinFlex>
            <span className={styles.hint}>
              {t('eudr-regulation.file-upload-pdf-type-description', {
                maxSize: MAX_SIZE_MB,
              })}
            </span>
          </DarwinFlex>
        </DarwinFlex>
      </DarwinUpload>
    </DarwinFlex>
  );
}
